import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import {
  Flex,
  LineChart,
  formatCurrency,
  formatNumber,
  CurrencyValue,
  DisplaySwitch
} from '@blockanalitica/ui';
import { useMemo, useState, useCallback } from 'react';
import { useChartPeriod } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';

export default function HistoricTabs() {
  const [activeDisplayOption, setActiveDisplayOption] = useState('reserves');
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();
  const displayOptions = useMemo(
    () => [
      { key: 'reserves', value: 'Reserves' },
      { key: 'debt', value: 'Debt' }
    ],
    []
  );

  const [data, error] = useApi({
    url: '/groups/stablecoins/historic/',
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  const valueFormatter = useCallback(
    (value: number) => {
      switch (activeDisplayOption) {
        case 'revenue':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: 'USD'
              }}
            />
          );
        case 'debt':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('DAI'),
                iconSize: '3'
              }}
            />
          );
        default:
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('USDC'),
                iconSize: '3'
              }}
            />
          );
      }
    },
    [activeDisplayOption]
  );
  const yScaleValueFormatter = useCallback(
    (value: number) => {
      switch (activeDisplayOption) {
        case 'revenue':
          return formatCurrency(value);
        default:
          return formatNumber(value);
      }
    },
    [activeDisplayOption]
  );

  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />

        <LineChart
          key={activeDisplayOption}
          data={data}
          datasets={[
            {
              xField: 'date',
              yField: activeDisplayOption
            }
          ]}
          timeFilter={timeFilter}
          config={config}
          valueFormatter={valueFormatter}
          yScaleValueFormatter={yScaleValueFormatter}
        />
      </Flex>
    );
  }
  return <ChartSkeleton />;
}
